// uiarc: pagination (https://uiarc.dev/r/pagination.json) — Tailwind'e çevrildi (CSS Modules yok).
// Beş sayfalık pencere: pencere kayınca numaralar kayış gibi aynı sayıda yuva kayar; tek bir işaret (kenarlıklı
// yüzey) geçerli sayfaya yayla süzülür. Ölçülene kadar geçerli düğme kendi kenarlığını çizer. Yalnız oklar basınca
// küçülür; numaralar `motion` ile hareket ettiğinden CSS onların `transform`/`opacity`'sine dokunmaz.
import { ChevronLeft, ChevronRight } from 'lucide-react'
import {
  AnimatePresence,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  type Variants,
} from 'motion/react'
import { useLayoutEffect, useRef, useState } from 'react'

import { motionTokens } from '@/lib/motion'
import { messages } from '@/lib/messages'
import { cn } from '@/lib/utils'

export interface PaginationProps {
  /** 1 tabanlı geçerli sayfa. */
  page: number
  pageCount: number
  onPageChange: (page: number) => void
  className?: string
  label?: string
  previousLabel?: string
  nextLabel?: string
  pageLabel?: (page: number) => string
}

/** Bir düğme + boşluk (36px + 4px), düğme genişliğinin yüzdesi olarak. */
const slot = 1000 / 9

const slide: Variants = {
  enter: (shift: number) => ({ opacity: 0, x: `${slot * shift}%` }),
  center: { opacity: 1, x: '0%' },
  exit: (shift: number) => ({
    opacity: 0,
    x: `${-slot * shift}%`,
    transition: {
      x: motionTokens.spring.smooth,
      opacity: { duration: motionTokens.duration.instant, ease: motionTokens.ease.standard },
    },
  }),
}

const buttonClass =
  'relative grid size-9 cursor-pointer place-items-center rounded-control border border-transparent bg-transparent text-xs text-text-secondary tabular-nums transition-colors duration-(--duration-fast) ease-standard hover:enabled:bg-surface-muted hover:enabled:text-foreground disabled:cursor-not-allowed disabled:opacity-40 aria-[current=page]:text-foreground motion-reduce:transition-none'
const stepClass =
  'transition-[background-color,color,opacity,transform] duration-(--duration-fast) ease-standard active:enabled:scale-[0.96] motion-reduce:active:enabled:scale-100'

function Pagination({
  page,
  pageCount,
  onPageChange,
  className,
  label = messages.pagination.navLabel,
  previousLabel = messages.pagination.previous,
  nextLabel = messages.pagination.next,
  pageLabel = messages.pagination.page,
}: PaginationProps) {
  const reduced = useReducedMotion() ?? false
  const navRef = useRef<HTMLElement>(null)
  const markX = useMotionValue(0)
  const markY = useMotionValue(0)
  const safePageCount = Math.max(0, Math.floor(pageCount))
  const currentPage = safePageCount > 0 ? Math.min(Math.max(1, Math.floor(page)), safePageCount) : 0
  const start = safePageCount > 0 ? Math.max(1, Math.min(safePageCount - 4, currentPage - 2)) : 1
  const [previousStart, setPreviousStart] = useState(start)
  const [shift, setShift] = useState(0)
  if (previousStart !== start) {
    setShift(start - previousStart)
    setPreviousStart(start)
  }
  const visible = Array.from({ length: Math.min(safePageCount, 5) }, (_, index) => start + index)

  // İşaret geçerli sayfanın son yuvasına süzülür; ilk ölçümde ve yeniden boyutlanmada anında yerleşir.
  useLayoutEffect(() => {
    const nav = navRef.current
    const current = nav?.querySelector<HTMLElement>("[aria-current='page']")
    if (!nav || !current) return
    const place = (instant: boolean) => {
      const x = current.offsetLeft
      const y = current.offsetTop
      if (instant || reduced) {
        markX.jump(x)
        markY.jump(y)
        return
      }
      void animate(markX, x, motionTokens.spring.morph)
      void animate(markY, y, motionTokens.spring.morph)
    }
    place(!('markReady' in nav.dataset))
    nav.dataset.markReady = ''
    let size = `${nav.offsetWidth}x${nav.offsetHeight}`
    const observer = new ResizeObserver(() => {
      const next = `${nav.offsetWidth}x${nav.offsetHeight}`
      if (next !== size) {
        size = next
        place(true)
      }
    })
    observer.observe(nav)
    return () => observer.disconnect()
  }, [currentPage, start, safePageCount, reduced, markX, markY])

  const navClass = cn(
    'relative flex flex-wrap items-center gap-1',
    // İşaret hazır değilken geçerli düğme kendi kenarlığını çizer.
    '[&:not([data-mark-ready])_[aria-current=page]]:border-border [&:not([data-mark-ready])_[aria-current=page]]:bg-surface [&:not([data-mark-ready])_[aria-current=page]]:shadow-resting',
    // İşaret hazırken geçerli düğmenin üzerine gelince zemin değişmez.
    '[&[data-mark-ready]_[aria-current=page]:hover]:bg-transparent!',
    className,
  )

  if (!safePageCount) return <nav className={navClass} aria-label={label} />

  return (
    <nav ref={navRef} className={navClass} aria-label={label}>
      <button
        type="button"
        className={cn(buttonClass, stepClass)}
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        aria-label={previousLabel}
      >
        <ChevronLeft className="size-4" aria-hidden="true" />
      </button>
      <motion.span
        aria-hidden="true"
        className="rounded-control border-border bg-surface shadow-resting pointer-events-none absolute top-0 left-0 size-9 border opacity-0 [nav[data-mark-ready]_&]:opacity-100"
        style={{ x: markX, y: markY }}
      />
      <AnimatePresence mode="popLayout" initial={false} custom={shift}>
        {visible.map((number) => (
          <motion.button
            type="button"
            key={number}
            className={buttonClass}
            onClick={() => onPageChange(number)}
            aria-label={pageLabel(number)}
            aria-current={currentPage === number ? 'page' : undefined}
            layout={reduced ? false : 'position'}
            layoutDependency={start}
            custom={shift}
            variants={slide}
            initial={reduced ? false : 'enter'}
            animate="center"
            exit={reduced ? undefined : 'exit'}
            transition={
              reduced
                ? { duration: 0 }
                : {
                    x: motionTokens.spring.smooth,
                    layout: motionTokens.spring.smooth,
                    opacity: {
                      duration: motionTokens.duration.standard,
                      ease: motionTokens.ease.enter,
                    },
                  }
            }
          >
            {number}
          </motion.button>
        ))}
      </AnimatePresence>
      <button
        type="button"
        className={cn(buttonClass, stepClass)}
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= safePageCount}
        aria-label={nextLabel}
      >
        <ChevronRight className="size-4" aria-hidden="true" />
      </button>
    </nav>
  )
}

export { Pagination }
